import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Badge, Table, Form, InputGroup, Alert, Spinner, Dropdown } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface Ticket {
  id: string
  subject: string
  description: string
  status: string
  priority: string
  category?: string
  createdAt: string
  updatedAt: string
  org?: {
    id: string
    name: string
    slug: string
  }
  requester: {
    name: string
    email: string
  }
  assignee?: {
    name: string
    email: string
  }
  tags: Array<{
    tag: {
      name: string
    }
  }>
  _count: {
    comments: number
    attachments: number
  }
}

export const MyTicketsPage = () => {
  const { user } = useAuth() // eslint-disable-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  useEffect(() => {
    fetchTickets()
  }, [statusFilter, priorityFilter, categoryFilter])

  const fetchTickets = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      
      if (statusFilter) params.append('status', statusFilter)
      if (priorityFilter) params.append('priority', priorityFilter)
      if (categoryFilter) params.append('category', categoryFilter)
      
      const response = await api.get(`/tickets?${params.toString()}`)
      setTickets(response.data.data || [])
    } catch (err: any) {
      setError('Failed to load tickets')
    } finally {
      setLoading(false)
    }
  }

  const filteredTickets = tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent': return 'danger'
      case 'High': return 'warning'
      case 'Medium': return 'info'
      case 'Low': return 'secondary'
      default: return 'secondary'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'info'
      case 'IN_PROGRESS': return 'warning'
      case 'PENDING_CUSTOMER': return 'danger'
      case 'RESOLVED': return 'success'
      case 'CLOSED': return 'secondary'
      case 'REOPENED': return 'danger'
      default: return 'secondary'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'OPEN': return '📨 Received'
      case 'IN_PROGRESS': return '⏳ We\'re Working On It'
      case 'PENDING_CUSTOMER': return '💬 Waiting For Your Reply'
      case 'RESOLVED': return '✅ Fixed!'
      case 'CLOSED': return '📁 Closed'
      case 'REOPENED': return '🔄 Reopened'
      default: return status
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div className="my-tickets-container">
      {/* Header */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
        <div className="mb-2 mb-sm-0">
          <h2 className="mb-2">
            <i className="fas fa-folder-open me-2 text-primary"></i>
            My Support Requests
          </h2>
          <p className="text-muted mb-0">View and manage all your help requests in one place</p>
        </div>
        <Button variant="success" size="lg" onClick={() => navigate('/tickets/new')} className="d-flex align-items-center px-4">
          <i className="fas fa-plus-circle me-2"></i>
          Get Help
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            
            <Col md={2}>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="OPEN">📨 New</option>
                <option value="IN_PROGRESS">⏳ Being Worked On</option>
                <option value="PENDING_CUSTOMER">💬 Need Your Reply</option>
                <option value="RESOLVED">✅ Fixed</option>
                <option value="CLOSED">📁 Closed</option>
              </Form.Select>
            </Col>
            
            <Col md={2}>
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <option value="">All Priority</option>
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </Form.Select>
            </Col>
            
            <Col md={2}>
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                <option value="Technical">Technical</option>
                <option value="Billing">Billing</option>
                <option value="Feature Request">Feature Request</option>
                <option value="General">General</option>
              </Form.Select>
            </Col>
            
            <Col md={2} className="d-flex align-items-end">
              <Button 
                variant="outline-secondary" 
                onClick={() => {
                  setStatusFilter('')
                  setPriorityFilter('')
                  setCategoryFilter('')
                  setSearchTerm('')
                }}
                className="w-100"
              >
                <i className="fas fa-times me-2"></i>Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tickets Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-ticket-alt me-2 text-primary"></i>
              Tickets ({filteredTickets.length})
            </h5>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={fetchTickets}
            >
              <i className="fas fa-sync-alt me-2"></i>Refresh
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {filteredTickets.length > 0 ? (
            <Table hover className="mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Subject</th>
                  {user?.role === 'ADMIN' && <th>Organization</th>}
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Category</th>
                  <th>Created</th>
                  <th>Activity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td style={{ maxWidth: '350px' }}>
                      <div style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        <div className="fw-medium" style={{ wordBreak: 'break-word' }}>{ticket.subject}</div>
                        <small className="text-muted" style={{ display: 'block', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                          {ticket.description.substring(0, 100)}
                          {ticket.description.length > 100 && '...'}
                        </small>
                      </div>
                    </td>
                    {user?.role === 'ADMIN' && (
                      <td>
                        <Badge bg="info" className="text-dark">
                          {ticket.org?.name || 'Unknown'}
                        </Badge>
                      </td>
                    )}
                    <td>
                      <Badge bg={getStatusColor(ticket.status)} className="px-3 py-2" style={{ fontSize: '0.85rem' }}>
                        {getStatusText(ticket.status)}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={getPriorityColor(ticket.priority)}>
                        {ticket.priority}
                      </Badge>
                    </td>
                    <td>
                      <span className="text-muted">
                        {ticket.category || 'General'}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div>{formatDate(ticket.createdAt)}</div>
                        <small className="text-muted">
                          by {ticket.requester.name}
                        </small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Badge 
                          bg="light" 
                          text="dark" 
                          className="d-flex align-items-center gap-1"
                          title={`${ticket._count.comments} comments`}
                        >
                          <i className="fas fa-comments"></i>
                          {ticket._count.comments}
                        </Badge>
                        <Badge 
                          bg={ticket._count.attachments > 0 ? 'info' : 'light'} 
                          text={ticket._count.attachments > 0 ? 'white' : 'dark'}
                          className="d-flex align-items-center gap-1"
                          title={`${ticket._count.attachments} attachment${ticket._count.attachments !== 1 ? 's' : ''}`}
                        >
                          <i className="fas fa-paperclip"></i>
                          {ticket._count.attachments}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                        >
                          <i className="fas fa-eye"></i>
                        </Button>
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <i className="fas fa-ellipsis-v"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => navigate(`/tickets/${ticket.id}`)}>
                              <i className="fas fa-eye me-2"></i>View Details
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => navigate(`/tickets/${ticket.id}#comments`)}>
                              <i className="fas fa-comments me-2"></i>View Comments
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item className="text-danger">
                              <i className="fas fa-archive me-2"></i>Archive
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-5">
              <div className="mb-4">
                <i className="fas fa-smile-beam text-primary" style={{ fontSize: '4rem', opacity: 0.5 }}></i>
              </div>
              <h4 className="text-muted mb-3">You don't have any support requests yet</h4>
              <p className="text-muted mb-4">Having an issue? We're here to help! Click the button below to get started.</p>
              <Button variant="success" size="lg" onClick={() => navigate('/tickets/new')} className="px-5">
                <i className="fas fa-life-ring me-2"></i>Get Help Now
              </Button>
              <div className="mt-4 text-muted small">
                <p className="mb-1"><i className="fas fa-bolt me-2"></i>Fast response times</p>
                <p className="mb-0"><i className="fas fa-users me-2"></i>Expert support team ready to assist</p>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

